import React, { useCallback, useContext, useEffect, useState, useRef } from 'react'
import SelectApi from "api/select-api";
import VideoCreationApi from "api/video-creation-api";
import ListPagination from "components/ListPagination";
import { Row, Spinner, Button} from "react-bootstrap";
import ImageGallery from "components/ImageGallery";
import { CaseImagesContext } from "./CaseImages";
import { createPortal } from "react-dom";
import CameraMediaApi from "api/camera-media-api";
import { GalleryItem } from "helpers/types";

const selectApi = new SelectApi()
const cameraMediaApi = new CameraMediaApi()
const videoCreationApi = new VideoCreationApi()

const CreateVideo = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(240);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [gallery, setGallery] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false);
    const pageImages = useRef<GalleryItem[]>([]);
    const {
        selectJob,
        contextMenuContainer,
        contextMenuContainerBottom,
        lockedImageIds,
        setLockedImageIds,
        imageRequestHD,
        onShowToastMessage: showToastMessage,
        showNotificationBanner,
        onGoToImage,
        captureDate,
        locationInfo,
        videoCreationImageSetImages,
        setVideoCreationImageSetImages
    } = useContext(CaseImagesContext);
    
    // Fetch select images
    useEffect(() => {
        const getSelectImages = async () => {
            if (selectJob === null || !selectJob.id) {
                return;
            }
            try {
                setIsLoading(true);
                const offset = (currentPage - 1) * pageSize;
                const {images, count} = await selectApi.fetchSelectImages(
                    selectJob.id,
                    {
                        limit: pageSize,
                        offset: offset
                    }
                );
                setGallery([...images]);
                pageImages.current = images.map( media => ({
                    ...media,
                    download_url: '',
                    image_src: ''
                }));
                setTotalPages(Math.ceil(count / pageSize));
                setTotalCount(count);
            } finally {
                setIsLoading(false);
            }
        }
        getSelectImages();
    }, [currentPage, pageSize, selectJob]);

    const onSetPageSize = useCallback((size: number) => {
        setPageSize(size);
    }, []);

    const onChangePage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    // Tick every image of the select job, across all pages, not just the page on
    // screen. Selection is client-side until Assign Video Images sends it. Ids
    // already ticked are kept, so a partial manual pick is never lost.
    const selectAllImages = async () => {
        if (selectJob === null || !selectJob.id) {
            return;
        }
        try {
            setIsLoading(true);
            const { images } = await selectApi.fetchSelectImages(
                selectJob.id,
                {
                    limit: Math.max(totalCount, pageSize),
                    offset: 0
                }
            );
            const allIds = images.map((image: any) => image.id);
            setVideoCreationImageSetImages((prevIds: number[]) =>
                Array.from(new Set([...prevIds, ...allIds]))
            );
            showToastMessage && showToastMessage(`${allIds.length} images selected for the video.`, 'info');
        } catch (e) {
            console.error('Error selecting all images:', e);
            showToastMessage && showToastMessage('Could not load the images to select.', 'danger');
        } finally {
            setIsLoading(false);
        }
    }

    const unselectAllImages = () => {
        setVideoCreationImageSetImages([]);
    }

    const createVideoFromSelectedImages = async () => {
        showToastMessage && showToastMessage('Video creation started. This may take a few minutes...', 'info');
        try {
            const result = await videoCreationApi.createVideoFromDateAndLocation(locationInfo.id, captureDate);
            if (result && result.exit_code !== undefined) {
                showNotificationBanner && showNotificationBanner('Video created successfully. Check the Video tab to view it.', 'success');
            } else {
                showNotificationBanner && showNotificationBanner('Video creation finished with an unexpected response. Check the Video tab to confirm.', 'warning');
            }
        } catch (e: any) {
            // Error replies arrive as thrown ApiErrors carrying the parsed body in e.data
            const errData = e?.data;
            if (errData && errData['UPLOAD ERROR']) {
                showNotificationBanner && showNotificationBanner(`Video creation failed: ${errData['UPLOAD ERROR']}`, 'danger');
            } else if (errData && errData.error) {
                const errorMsg = errData.error.includes('Video image set not found')
                    ? 'Video image set not found. Please assign images to the video first.'
                    : `Video creation failed: ${errData.error}`;
                showNotificationBanner && showNotificationBanner(errorMsg, 'danger');
            } else {
                // No structured error means the connection dropped mid-wait. The server
                // does not abort the render, so do not claim failure.
                console.error('Error creating video:', e);
                showNotificationBanner && showNotificationBanner('Lost connection while the video was being created — it may still finish. Check the Video tab in a few minutes.', 'warning');
            }
        }
    }

    const assignImagesToVideoCreationImageSetButton = async () => {
        //const locationId = 2;  // in state var locationId
        //const imageIds = [245, 246, 247, 248, 249, 250]; // state var videoCreationImageSetImages

        // Nothing selected: bail before any server call. The add endpoint rejects
        // empty lists, and reaching it would first create an empty image set as a
        // side effect on days that do not have one yet.
        if (!videoCreationImageSetImages || videoCreationImageSetImages.length === 0) {
            return {
                success: false,
                message: 'No images are selected for the video. Select images and try again.'
            };
        }

        try {
            let imageSetId = 0;
            const getIDresp = await videoCreationApi.getVideoCreationImageSetID(locationInfo.id, captureDate)
            if (getIDresp && getIDresp.vid_creation_img_set_id ) {
                imageSetId = getIDresp.vid_creation_img_set_id;
            } else {
                // Step 1: Create a video image set
                const createResponse = await videoCreationApi.createVideoImageSet({
                    location_id: locationInfo.id,
                    capture_date: captureDate,
                    region_of_interest: {
                        relative_x: 0,
                        relative_y: 0,
                        relative_width: 1,
                        relative_height: 1
                    }
                });
                if (!createResponse || !createResponse) {
                    return { 
                        success: false, 
                        message: "Failed to create video image set" 
                    };
                }
                
                imageSetId = createResponse.id;
            }
            if (imageSetId < 1){
                return { 
                    success: false, 
                    message: 'Error: there was an error in retreiving the video image set ID.' 
                };
            }
            // Step 2: Add images to the video set
            const addImagesResponse = await videoCreationApi.addImagesToVideoSet({
                image_set_id: imageSetId,
                image_ids: videoCreationImageSetImages
            });
            
            if (!addImagesResponse) {
                return { 
                    success: false, 
                    message: "Failed to add images to video set",
                    imageSetId: imageSetId
                };
            }
            
            return { 
                success: true, 
                message: "Successfully created video image set and added images",
                imageSetId: imageSetId
            };
        } catch (error: any) {
            console.error("Error in assignImagesToVideoCreationImageSetButton:", error);
            // Server error replies arrive as thrown ApiError objects, which are not
            // Error instances - String(error) renders "[object Object]". The readable
            // message lives in error.data.error / error.data.message / error.text.
            const serverMessage = error?.data?.error || error?.data?.message || error?.text
                || (error instanceof Error ? error.message : '');
            return {
                success: false,
                message: serverMessage ? `Error: ${serverMessage}` : 'Failed to assign images to video.'
            };
        }
    }

    // Runs the assign action and announces the result. Used by the toolbar
    // buttons and by the image popup's Assign Video Images button, so the two
    // entry points can never behave differently.
    const assignVideoImagesWithNotification = async () => {
        const result = await assignImagesToVideoCreationImageSetButton();
        if (result && result.success) {
            showToastMessage && showToastMessage('Images assigned to video.', 'success');
        } else {
            showToastMessage && showToastMessage((result && result.message) || 'Failed to assign images to video.', 'danger');
        }
    };

    const removeVideoCreationImageSetButton = async () => {
        try {
            let imageSetId = 0;
            const getIDresp = await videoCreationApi.getVideoCreationImageSetID(locationInfo.id, captureDate)
            if (getIDresp && getIDresp.vid_creation_img_set_id ) {
                imageSetId = getIDresp.vid_creation_img_set_id;
                const deleteSetResp = await videoCreationApi.deleteVideoImageSet(imageSetId)
                if (deleteSetResp.deleted_id) {
                    setVideoCreationImageSetImages([])
                } else {
                    console.log("Delete status code WAS NOT 200")
                }
            } else {
                console.log("removeVideoCreationImageSetButton - Could not find the set of images for video creation.")
            }
        } catch (error) {
            
        }    
    }

    const testVideoCreation = async () => {
        try {
            const result = await videoCreationApi.testVideoCreation();
            
            if (result.success) {
                alert(`Test successful! Image set ID: ${result.imageSetId}`);
            } else {
                alert(`Test failed: ${result.message}`);
            }
        } catch (error) {
            console.error("Error testing video creation:", error);
            alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    return (
        <React.Fragment>


            {/* These page-level buttons render into both gallery toolbars (top and bottom). */}
            {
                [contextMenuContainer, contextMenuContainerBottom].map((container, i) =>
                    container?.current &&
                    createPortal(
                        <React.Fragment>
                            {
                                <Button className="filter-menu-item top-toolbar-button top-toolbar-button-lr-spacing select-all-video-images"
                                onClick={ () => selectAllImages() }>
                                    Select All
                                </Button>
                            }
                        </React.Fragment>,
                        container.current,
                        `select-all-video-images-button-${i}`
                    )
                )
            }

            {
                [contextMenuContainer, contextMenuContainerBottom].map((container, i) =>
                    container?.current &&
                    createPortal(
                        <React.Fragment>
                            {
                                <Button className="filter-menu-item top-toolbar-button top-toolbar-button-lr-spacing unselect-all-video-images"
                                onClick={ () => unselectAllImages() }>
                                    Unselect All
                                </Button>
                            }
                        </React.Fragment>,
                        container.current,
                        `unselect-all-video-images-button-${i}`
                    )
                )
            }

            {
                [contextMenuContainer, contextMenuContainerBottom].map((container, i) =>
                    container?.current &&
                    createPortal(
                        <React.Fragment>
                            {
                                <Button className="filter-menu-item top-toolbar-button top-toolbar-button-lr-spacing add-images-to-video-creation-image-set"
                                onClick={ () => assignVideoImagesWithNotification() }>
                                    Assign Video Images
                                </Button>
                            }
                        </React.Fragment>,
                        container.current,
                        `test-video-creation-button-${i}`
                    )
                )
            }

            {
                [contextMenuContainer, contextMenuContainerBottom].map((container, i) =>
                    container?.current &&
                    createPortal(
                        <React.Fragment>
                            {
                                <Button className="filter-menu-item top-toolbar-button top-toolbar-button-lr-spacing delete-video-creation-image-set"
                                onClick={ () => removeVideoCreationImageSetButton() }>
                                    Unassign Assigned Video Images
                                </Button>
                            }
                        </React.Fragment>,
                        container.current,
                        `video-creation-image-set-deletion-button-${i}`
                    )
                )
            }

            {
                [contextMenuContainer, contextMenuContainerBottom].map((container, i) =>
                    container?.current &&
                    createPortal(
                        <React.Fragment>
                            {
                                <Button className="filter-menu-item top-toolbar-button top-toolbar-button-lr-spacing create-video"
                                onClick={ () => createVideoFromSelectedImages() }>
                                    Finalize Video
                                </Button>
                            }
                        </React.Fragment>,
                        container.current,
                        `create-video-button-${i}`
                    )
                )
            }

            <ListPagination
                className="pagination-top"
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onSetPageSize={onSetPageSize}
                onChangePage={onChangePage}
                onGoToImage={onGoToImage}
            />

            <Row className="filterable-content position-relative">
                {isLoading &&
                    <div className="d-flex justify-content-center">
                        <Spinner className="text-primary m-2"/>
                    </div>
                }
                {
                    !isLoading && gallery.length === 0 &&
                    <span className="text-muted">No select images found</span>
                }
                <ImageGallery
                    name="Create Video"
                    images={gallery}
                    onAssignVideoImages={assignVideoImagesWithNotification}/>
            </Row>

            <ListPagination
                className="pagination-bottom"
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onSetPageSize={onSetPageSize}
                onChangePage={onChangePage}
                onGoToImage={onGoToImage}    
            />
        </React.Fragment>
    );
}

export default React.memo(CreateVideo); 